import { useState, useEffect } from 'react';
import { AreaChart, Area, XAxis, YAxis, ResponsiveContainer, Tooltip, BarChart, Bar } from 'recharts';
import {
  LayoutDashboard, Target, Database, BookOpen, Zap,
  TrendingUp, Users, CheckCircle2, Star,
  Plus, MapPin, FileText, Video, Download, Activity,
  ChevronRight, ArrowUpRight, Shield, Clock, Search,
  Filter, MoreHorizontal, Bell, Settings
} from 'lucide-react';
import {
  INFORMERS, HORECA_LOCATIONS, DAILY_PLAN, HOURLY_DCC, LIVE_FEED, KNOWLEDGE_DOCS
} from './data';

// ─── Design tokens (match pmi.wibe.si) ───────────────────────────────────────
const C = {
  bg:      '#080f1e',
  surface: 'rgba(255,255,255,0.035)',
  border:  'rgba(255,255,255,0.08)',
  accent:  '#C87533',
  accentL: 'rgba(200,117,51,0.15)',
  blue:    '#38BDF8',
  blueL:   'rgba(56,189,248,0.12)',
  purple:  '#A78BFA',
  green:   '#22C55E',
};

const regionColor = r => r === 'Center' ? C.accent : r === 'Zahod' ? C.blue : C.purple;

// ─── Slovenia Live Map ────────────────────────────────────────────────────────
const SloveniaLiveMap = ({ informers }) => {
  const border = "M0.0,77.6 L4.4,86.1 L3.3,89.1 L14.3,88.2 L17.8,92.6 L26.9,93.7 L25.0,99.1 L11.3,109.7 L12.4,112.9 L9.4,116.7 L14.4,122.1 L24.9,119.8 L18.6,137.2 L20.5,143.3 L39.0,151.8 L50.6,166.7 L43.2,173.5 L23.6,166.9 L0.8,175.7 L27.9,192.3 L38.3,189.1 L40.9,193.4 L47.7,194.5 L50.2,190.8 L56.8,190.5 L54.6,183.8 L58.3,181.9 L70.9,187.9 L77.5,188.7 L80.3,183.8 L88.2,188.5 L93.5,186.2 L103.9,179.5 L104.9,170.1 L108.0,169.3 L111.3,161.4 L114.8,162.6 L113.6,167.5 L122.5,173.8 L122.5,181.0 L132.3,184.2 L134.3,190.0 L142.8,187.6 L144.9,180.6 L159.3,186.8 L167.8,195.1 L187.2,186.4 L179.4,179.8 L177.1,170.6 L189.1,164.5 L185.7,158.6 L183.7,161.6 L184.6,155.9 L181.4,161.3 L178.1,158.8 L175.2,155.2 L176.9,152.4 L195.1,145.0 L194.7,141.9 L200.5,141.2 L201.5,138.0 L211.1,141.6 L217.4,138.1 L214.4,135.1 L214.6,130.3 L217.2,128.0 L216.5,117.9 L219.5,111.6 L209.1,105.5 L206.8,97.9 L211.6,88.4 L222.8,89.5 L225.0,82.8 L245.8,76.1 L251.6,71.2 L249.8,64.8 L272.5,66.8 L266.9,50.7 L278.7,43.1 L293.0,53.4 L300.0,53.7 L293.2,49.8 L291.8,41.8 L280.9,32.6 L284.3,24.5 L278.8,23.1 L274.1,16.4 L273.6,10.6 L276.8,4.8 L266.1,0.0 L243.1,6.6 L243.3,18.2 L247.2,22.1 L248.0,29.6 L229.5,20.7 L206.7,25.0 L198.1,35.2 L189.9,29.6 L155.1,30.2 L149.4,36.9 L147.2,32.7 L143.8,36.8 L139.2,36.1 L134.4,49.2 L124.7,50.5 L123.3,55.4 L113.7,59.0 L110.7,67.6 L106.8,60.3 L102.8,62.1 L98.2,57.6 L73.4,59.4 L66.7,52.4 L31.5,47.4 L29.2,58.5 L20.7,58.6 L17.7,64.2 L5.9,69.3 L6.8,73.1 Z";
  const toSVG = (lat, lng) => ({
    x: ((lng - 13.38) / (16.60 - 13.38)) * 300,
    y: ((46.88 - lat) / (46.88 - 45.42)) * 195,
  });
  return (
    <svg viewBox="0 0 300 195" className="w-full h-auto">
      <defs>
        <radialGradient id="mapBg" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#C87533" stopOpacity="0.06" />
          <stop offset="100%" stopColor="#C87533" stopOpacity="0" />
        </radialGradient>
        <filter id="dotGlow">
          <feGaussianBlur stdDeviation="1.5" result="blur"/>
          <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>
      </defs>
      <rect width="300" height="195" fill="url(#mapBg)" />
      <path d={border} fill="rgba(200,117,51,0.06)" stroke="rgba(200,117,51,0.25)" strokeWidth="0.8" strokeLinejoin="round" />
      {informers.map(inf => {
        const { x, y } = toSVG(inf.lat, inf.lng);
        const c = regionColor(inf.region);
        return (
          <g key={inf.id} filter="url(#dotGlow)">
            <circle cx={x} cy={y} r="5" fill={c} opacity="0.15" />
            <circle cx={x} cy={y} r="3.5" fill={c} opacity="0.9" />
            <circle cx={x} cy={y} r="7" fill="none" stroke={c} strokeWidth="0.8" opacity="0.4">
              <animate attributeName="r" values="3.5;11;3.5" dur={`${2.5 + inf.id * 0.3}s`} repeatCount="indefinite" />
              <animate attributeName="opacity" values="0.4;0;0.4" dur={`${2.5 + inf.id * 0.3}s`} repeatCount="indefinite" />
            </circle>
          </g>
        );
      })}
    </svg>
  );
};

// ─── DCC Ring ─────────────────────────────────────────────────────────────────
const DCCRing = ({ value, target }) => {
  const pct = Math.min(value / target, 1);
  const r = 38, circ = 2 * Math.PI * r;
  const done = pct >= 1;
  return (
    <div className="relative w-28 h-28 mx-auto">
      <svg viewBox="0 0 96 96" className="w-full h-full -rotate-90">
        <circle cx="48" cy="48" r={r} fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="5" />
        <circle cx="48" cy="48" r={r} fill="none"
          stroke={done ? C.green : C.accent} strokeWidth="5"
          strokeDasharray={circ} strokeDashoffset={circ * (1 - pct)}
          strokeLinecap="round" style={{ transition: 'stroke-dashoffset 0.8s ease', filter: `drop-shadow(0 0 4px ${done ? C.green : C.accent})` }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-2xl font-bold text-white leading-none">{value}</span>
        <span className="text-[9px] text-white/35 font-mono mt-0.5">/{target}</span>
      </div>
    </div>
  );
};

// ─── KPI Card ─────────────────────────────────────────────────────────────────
const KpiCard = ({ icon: Icon, label, value, sub, color, delta }) => (
  <div className="relative rounded-2xl p-5 overflow-hidden group transition-all duration-300 hover:-translate-y-0.5"
    style={{ background: C.surface, border: `1px solid ${C.border}` }}>
    <div className="absolute top-0 right-0 w-24 h-24 rounded-full blur-2xl pointer-events-none transition-opacity duration-300 group-hover:opacity-100 opacity-60"
      style={{ background: color, transform: 'translate(30%,-30%)' }} />
    <div className="relative">
      <div className="flex items-start justify-between mb-4">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${color}20` }}>
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
        {delta !== undefined && (
          <span className="text-[11px] font-mono px-2 py-0.5 rounded-full"
            style={{ background: delta >= 0 ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)', color: delta >= 0 ? C.green : '#EF4444' }}>
            {delta >= 0 ? '+' : ''}{delta}%
          </span>
        )}
      </div>
      <p className="text-3xl font-bold text-white tracking-tight">{value}</p>
      <p className="text-xs text-white/45 mt-1 font-mono uppercase tracking-wide">{label}</p>
      {sub && <p className="text-[10px] text-white/25 mt-0.5">{sub}</p>}
    </div>
  </div>
);

// ─── Admin Dashboard ──────────────────────────────────────────────────────────
const AdminDashboard = () => {
  const [feed, setFeed] = useState(LIVE_FEED);
  const [total, setTotal] = useState(127);
  const [newEntry, setNewEntry] = useState(null);

  useEffect(() => {
    const names = ['Maja K.','Luka N.','Sara V.','Alen R.','Nina K.','Matej Z.','Petra H.'];
    const places = ['Bar Nova, LJ','Caffe Sky, MB','Hotel Arc, CE','Pub Mix, KP','Lounge X, NG','Bistro Sud, NM'];
    const regions = ['Center','Zahod','Vzhod'];
    const iv = setInterval(() => {
      const e = {
        id: Date.now(),
        informer: names[Math.floor(Math.random()*names.length)],
        dcc: Math.floor(Math.random()*4+1),
        location: places[Math.floor(Math.random()*places.length)],
        time: new Date().toLocaleTimeString('sl-SI',{hour:'2-digit',minute:'2-digit'}),
        region: regions[Math.floor(Math.random()*regions.length)],
      };
      setNewEntry(e.id);
      setFeed(p => [e,...p.slice(0,7)]);
      setTotal(p => p + e.dcc);
      setTimeout(() => setNewEntry(null), 800);
    }, 4000);
    return () => clearInterval(iv);
  }, []);

  return (
    <div className="space-y-5">
      {/* KPIs */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        <KpiCard icon={Target}   label="DCC danes"            value={total}   delta={12}  color={C.accent} sub="Cilj: 160 skupaj" />
        <KpiCard icon={Users}    label="Aktivni informatorji" value="18 / 20" color={C.blue}               sub="2 na odmoru" />
        <KpiCard icon={Star}     label="Sentiment"            value="4.6 / 5" delta={3}   color={C.purple} sub="HoReCa povprečje" />
        <KpiCard icon={Users}    label="Registrirani uporabniki" value="8 / 10"              color={C.green}  sub="2 prosta mesta" />
      </div>

      <div className="grid xl:grid-cols-5 gap-5">
        {/* Map */}
        <div className="xl:col-span-3 rounded-2xl p-5" style={{ background: C.surface, border: `1px solid ${C.border}` }}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-white">Terenska slika — live</span>
              <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono"
                style={{ background: 'rgba(34,197,94,0.1)', color: C.green }}>
                <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse inline-block" /> LIVE
              </span>
            </div>
            <div className="flex items-center gap-3">
              {['Center','Zahod','Vzhod'].map(r => (
                <span key={r} className="flex items-center gap-1.5 text-[10px] text-white/40 font-mono">
                  <span className="w-2 h-2 rounded-full" style={{ background: regionColor(r) }} />{r}
                </span>
              ))}
            </div>
          </div>
          <SloveniaLiveMap informers={INFORMERS} />
        </div>

        {/* Live Feed */}
        <div className="xl:col-span-2 rounded-2xl p-5 flex flex-col" style={{ background: C.surface, border: `1px solid ${C.border}` }}>
          <div className="flex items-center gap-2 mb-4">
            <Zap className="w-4 h-4" style={{ color: C.accent }} />
            <span className="text-sm font-semibold text-white">Live aktivnost</span>
          </div>
          <div className="space-y-2 flex-1">
            {feed.map((item, i) => (
              <div key={item.id}
                className="flex items-center gap-3 p-3 rounded-xl transition-all duration-500"
                style={{
                  background: i === 0 && item.id === newEntry ? `rgba(200,117,51,0.12)` : i === 0 ? `rgba(200,117,51,0.08)` : 'rgba(255,255,255,0.02)',
                  border: `1px solid ${i === 0 ? 'rgba(200,117,51,0.2)' : 'transparent'}`,
                }}>
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-bold flex-shrink-0"
                  style={{ background: `${regionColor(item.region)}20`, color: regionColor(item.region) }}>
                  {item.informer.slice(0,2)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-white">{item.informer}</p>
                  <p className="text-[10px] text-white/35 truncate">{item.location}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-base font-bold" style={{ color: C.accent }}>+{item.dcc}</p>
                  <p className="text-[10px] text-white/25 font-mono">{item.time}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Chart + Team */}
      <div className="grid xl:grid-cols-5 gap-5">
        <div className="xl:col-span-3 rounded-2xl p-5" style={{ background: C.surface, border: `1px solid ${C.border}` }}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4" style={{ color: C.accent }} />
              <span className="text-sm font-semibold text-white">DCC po urah</span>
            </div>
            <span className="text-[11px] text-white/30 font-mono">Danes</span>
          </div>
          <ResponsiveContainer width="100%" height={150}>
            <AreaChart data={HOURLY_DCC} margin={{ top: 5, right: 5, bottom: 0, left: -20 }}>
              <defs>
                <linearGradient id="aGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={C.accent} stopOpacity={0.25} />
                  <stop offset="95%" stopColor={C.accent} stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="h" tick={{ fill: 'rgba(255,255,255,0.25)', fontSize: 10, fontFamily: 'monospace' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: 'rgba(255,255,255,0.25)', fontSize: 10 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ background: '#0a1628', border: `1px solid rgba(200,117,51,0.3)`, borderRadius: 10, fontSize: 11, color: '#fff' }} cursor={{ stroke: 'rgba(200,117,51,0.2)', strokeWidth: 1 }} />
              <Area type="monotone" dataKey="dcc" stroke={C.accent} strokeWidth={2} fill="url(#aGrad)" dot={false} activeDot={{ r: 4, fill: C.accent, strokeWidth: 0 }} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="xl:col-span-2 rounded-2xl p-5" style={{ background: C.surface, border: `1px solid ${C.border}` }}>
          <div className="flex items-center gap-2 mb-4">
            <Users className="w-4 h-4" style={{ color: C.blue }} />
            <span className="text-sm font-semibold text-white">Ekipa</span>
          </div>
          <div className="space-y-3">
            {INFORMERS.map(inf => (
              <div key={inf.id} className="flex items-center gap-3">
                <div className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0"
                  style={{ background: `${regionColor(inf.region)}20`, color: regionColor(inf.region) }}>
                  {inf.avatar}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-[11px] text-white/80 truncate">{inf.name}</p>
                    <p className="text-[11px] font-bold ml-2" style={{ color: inf.dcc >= inf.target ? C.green : C.accent }}>{inf.dcc}</p>
                  </div>
                  <div className="h-1 rounded-full bg-white/5 overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-1000"
                      style={{ width: `${Math.min(inf.dcc/inf.target*100,100)}%`, background: inf.dcc >= inf.target ? C.green : regionColor(inf.region) }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Field View ───────────────────────────────────────────────────────────────
const FieldView = () => {
  const [showModal, setShowModal] = useState(false);
  const [dcc, setDcc] = useState(17);
  const [type, setType] = useState(null);

  const confirm = () => { if (!type) return; setDcc(d => d+1); setShowModal(false); setType(null); };

  return (
    <div className="space-y-5">
      {/* Top row */}
      <div className="grid grid-cols-3 gap-4">
        <div className="rounded-2xl p-5 flex flex-col items-center justify-center" style={{ background: C.surface, border: `1px solid ${C.border}` }}>
          <DCCRing value={dcc} target={20} />
          <p className="text-[10px] text-white/35 mt-3 font-mono uppercase tracking-wider">DCC Cilj / Izmena</p>
        </div>
        <div className="col-span-2 rounded-2xl p-5 flex flex-col justify-between" style={{ background: C.surface, border: `1px solid ${C.border}` }}>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="w-2 h-2 rounded-full bg-orange-400 animate-pulse" />
              <span className="text-[10px] text-orange-400 font-mono uppercase tracking-wider">Aktivna lokacija</span>
            </div>
            <p className="text-xl font-bold text-white">Bar Fusion</p>
            <p className="text-sm text-white/40 mt-0.5">Trubarjeva 22, Ljubljana</p>
            <div className="flex items-center gap-3 mt-3">
              <span className="text-[10px] px-2 py-0.5 rounded-full font-mono" style={{ background: C.accentL, color: C.accent }}>Bar</span>
              <span className="text-[10px] px-2 py-0.5 rounded-full font-mono bg-red-500/10 text-red-400">HIGH PRIORITY</span>
            </div>
          </div>
          <button onClick={() => setShowModal(true)}
            className="w-full mt-4 py-3.5 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all active:scale-95 hover:brightness-110"
            style={{ background: `linear-gradient(135deg, ${C.accent}, #a85f1f)`, color: '#fff', boxShadow: `0 4px 20px ${C.accent}40` }}>
            <Plus className="w-4 h-4" /> Dodaj DCC kontakt
          </button>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)' }}>
          <div className="w-full max-w-sm rounded-2xl p-6" style={{ background: '#0a1628', border: `1px solid ${C.accent}40` }}>
            <h3 className="text-lg font-bold text-white mb-1">Nov DCC kontakt</h3>
            <p className="text-xs text-white/35 mb-4 font-mono">Izberi tip potrošnika</p>
            <div className="grid grid-cols-3 gap-2 mb-5">
              {[['LAU','Polnoletni'],['LAS','Kadilec'],['LANU','Nekadilec']].map(([t, d]) => (
                <button key={t} onClick={() => setType(t)}
                  className="py-3 px-2 rounded-xl transition-all text-center"
                  style={{
                    background: type === t ? C.accent : 'rgba(255,255,255,0.05)',
                    border: `1px solid ${type === t ? C.accent : 'rgba(255,255,255,0.08)'}`,
                    color: type === t ? '#fff' : 'rgba(255,255,255,0.5)',
                    boxShadow: type === t ? `0 4px 16px ${C.accent}40` : 'none',
                  }}>
                  <p className="font-bold text-sm">{t}</p>
                  <p className="text-[9px] opacity-70 mt-0.5">{d}</p>
                </button>
              ))}
            </div>
            <div className="flex gap-3">
              <button onClick={() => { setShowModal(false); setType(null); }}
                className="flex-1 py-3 rounded-xl text-sm text-white/50 transition-all hover:text-white"
                style={{ background: 'rgba(255,255,255,0.05)', border: `1px solid ${C.border}` }}>
                Prekliči
              </button>
              <button onClick={confirm} disabled={!type}
                className="flex-1 py-3 rounded-xl text-sm font-bold text-white transition-all disabled:opacity-30"
                style={{ background: type ? C.accent : 'rgba(200,117,51,0.3)', boxShadow: type ? `0 4px 16px ${C.accent}40` : 'none' }}>
                Potrdi ✓
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="grid xl:grid-cols-2 gap-5">
        {/* Daily Plan */}
        <div className="rounded-2xl p-5" style={{ background: C.surface, border: `1px solid ${C.border}` }}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4" style={{ color: C.accent }} />
              <span className="text-sm font-semibold text-white">Dnevni načrt</span>
            </div>
            <span className="text-[11px] font-mono px-2.5 py-1 rounded-full" style={{ background: C.accentL, color: C.accent }}>
              {DAILY_PLAN.filter(l=>l.done).length} / 15
            </span>
          </div>
          <div className="space-y-1.5 max-h-80 overflow-y-auto pr-1" style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(200,117,51,0.2) transparent' }}>
            {DAILY_PLAN.map(loc => (
              <div key={loc.id}
                className="flex items-center gap-3 p-2.5 rounded-xl transition-all"
                style={{
                  background: loc.current ? `rgba(200,117,51,0.1)` : loc.done ? 'rgba(255,255,255,0.01)' : 'rgba(255,255,255,0.025)',
                  border: `1px solid ${loc.current ? 'rgba(200,117,51,0.25)' : 'transparent'}`,
                  opacity: loc.done && !loc.current ? 0.55 : 1,
                }}>
                <span className="text-[9px] text-white/25 font-mono w-9 flex-shrink-0">{loc.time}</span>
                {loc.done
                  ? <CheckCircle2 className="w-3.5 h-3.5 text-green-400 flex-shrink-0" />
                  : loc.current
                    ? <div className="w-3.5 h-3.5 rounded-full flex-shrink-0 animate-pulse" style={{ background: C.accent }} />
                    : <div className="w-3.5 h-3.5 rounded-full border flex-shrink-0" style={{ borderColor: C.border }} />}
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] font-semibold truncate" style={{ color: loc.current ? '#f5a96a' : '#fff' }}>{loc.name}</p>
                  <p className="text-[9px] text-white/30 truncate">{loc.address}</p>
                </div>
                <span className="text-[9px] font-mono flex-shrink-0"
                  style={{ color: loc.priority === 'HIGH' ? C.accent : loc.priority === 'MED' ? C.blue : 'rgba(255,255,255,0.2)' }}>
                  {loc.priority}
                </span>
                {loc.done && <span className="text-xs font-bold text-green-400 flex-shrink-0">+{loc.dcc}</span>}
              </div>
            ))}
          </div>
        </div>

        {/* Knowledge Hub */}
        <div className="rounded-2xl p-5" style={{ background: C.surface, border: `1px solid ${C.border}` }}>
          <div className="flex items-center gap-2 mb-4">
            <BookOpen className="w-4 h-4" style={{ color: C.purple }} />
            <span className="text-sm font-semibold text-white">Knowledge Hub</span>
          </div>
          <div className="space-y-2">
            {KNOWLEDGE_DOCS.map(doc => (
              <div key={doc.id}
                className="flex items-center gap-3 p-3 rounded-xl cursor-pointer group transition-all"
                style={{ background: 'rgba(255,255,255,0.02)', border: `1px solid transparent` }}
                onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'}
                onMouseLeave={e => e.currentTarget.style.borderColor = 'transparent'}>
                <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: doc.type === 'VIDEO' ? `${C.purple}15` : `${C.blue}15` }}>
                  {doc.type === 'VIDEO'
                    ? <Video className="w-4 h-4" style={{ color: C.purple }} />
                    : <FileText className="w-4 h-4" style={{ color: C.blue }} />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-white font-medium truncate">{doc.title}</p>
                  <p className="text-[10px] text-white/30">{doc.size} · {doc.updated}</p>
                </div>
                <span className="text-[9px] px-2 py-0.5 rounded-full font-mono flex-shrink-0"
                  style={{ background: C.accentL, color: C.accent }}>{doc.tag}</span>
                <Download className="w-3.5 h-3.5 text-white/20 group-hover:text-white/50 transition-colors flex-shrink-0" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── HoReCa DB ────────────────────────────────────────────────────────────────
const statusStyle = s => s === 'Obiskan' ? { bg: 'rgba(34,197,94,0.1)', c: '#22C55E' }
  : s === 'Prioriteta' ? { bg: C.accentL, c: C.accent }
  : s === 'Čakanje'    ? { bg: C.blueL,   c: C.blue }
  : { bg: 'rgba(255,255,255,0.05)', c: 'rgba(255,255,255,0.3)' };

const HoRecaDB = () => {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('Vse');
  const filtered = HORECA_LOCATIONS.filter(l =>
    (filter === 'Vse' || l.status === filter) &&
    l.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/25" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Išči lokacijo, mesto..."
            className="w-full pl-9 pr-4 py-2.5 text-sm text-white placeholder-white/25 rounded-xl outline-none transition-all focus:ring-1"
            style={{ background: C.surface, border: `1px solid ${C.border}`, focusRingColor: C.accent }}
            onFocus={e => e.target.style.borderColor = 'rgba(200,117,51,0.4)'}
            onBlur={e => e.target.style.borderColor = C.border} />
        </div>
        <div className="flex gap-2">
          {['Vse','Prioriteta','Obiskan','Čakanje','Redni'].map(f => {
            const active = filter === f;
            return (
              <button key={f} onClick={() => setFilter(f)}
                className="px-3.5 py-2.5 rounded-xl text-xs font-mono transition-all whitespace-nowrap"
                style={{
                  background: active ? C.accent : C.surface,
                  border: `1px solid ${active ? C.accent : C.border}`,
                  color: active ? '#fff' : 'rgba(255,255,255,0.4)',
                  boxShadow: active ? `0 4px 12px ${C.accent}35` : 'none',
                }}>
                {f}
              </button>
            );
          })}
        </div>
      </div>

      <div className="rounded-2xl overflow-hidden" style={{ border: `1px solid ${C.border}` }}>
        {/* Header */}
        <div className="grid grid-cols-12 gap-2 px-5 py-3"
          style={{ background: 'rgba(200,117,51,0.06)', borderBottom: `1px solid ${C.border}` }}>
          {[['4','IME'],['2','MESTO'],['1','TIP'],['1','DCC'],['2','INFORMATOR'],['2','STATUS']].map(([span, lbl]) => (
            <span key={lbl} className={`col-span-${span} text-[10px] text-white/30 font-mono uppercase tracking-wider ${lbl==='DCC'||lbl==='STATUS'?'text-right':''}`}>{lbl}</span>
          ))}
        </div>
        <div className="max-h-[480px] overflow-y-auto" style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(200,117,51,0.15) transparent' }}>
          {filtered.map((loc, i) => {
            const ss = statusStyle(loc.status);
            return (
              <div key={loc.id}
                className="grid grid-cols-12 gap-2 px-5 py-3 transition-all cursor-pointer text-xs"
                style={{ borderBottom: `1px solid rgba(255,255,255,0.03)`, background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)' }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(200,117,51,0.05)'}
                onMouseLeave={e => e.currentTarget.style.background = i%2===0?'transparent':'rgba(255,255,255,0.01)'}>
                <span className="col-span-4 text-white/85 font-medium truncate">{loc.name}</span>
                <span className="col-span-2 text-white/40 truncate">{loc.city}</span>
                <span className="col-span-1 text-white/30">{loc.type}</span>
                <span className="col-span-1 text-right font-bold" style={{ color: C.accent }}>{loc.dcc}</span>
                <span className="col-span-2 text-white/35 truncate">{loc.informer}</span>
                <span className="col-span-2 text-right">
                  <span className="inline-block text-[10px] px-2 py-0.5 rounded-full font-mono"
                    style={{ background: ss.bg, color: ss.c }}>{loc.status}</span>
                </span>
              </div>
            );
          })}
        </div>
        <div className="px-5 py-2.5 flex items-center justify-between"
          style={{ background: 'rgba(255,255,255,0.02)', borderTop: `1px solid ${C.border}` }}>
          <span className="text-[11px] text-white/25 font-mono">{filtered.length} lokacij · skupaj 1.500+</span>
          <span className="text-[11px] text-white/20 font-mono">Wibe Intel · 2026</span>
        </div>
      </div>
    </div>
  );
};

// ─── Intel App Shell ──────────────────────────────────────────────────────────
const VIEWS = [
  { id: 'admin',  label: 'Dashboard',  icon: LayoutDashboard },
  { id: 'field',  label: 'Field View', icon: Target },
  { id: 'horeca', label: 'HoReCa DB',  icon: Database },
];

export const IntelApp = () => {
  const [view, setView] = useState('admin');
  const [time, setTime] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setTime(new Date()), 1000); return () => clearInterval(t); }, []);

  return (
    <div className="min-h-screen flex flex-col" style={{ background: C.bg, color: '#fff', fontFamily: 'system-ui,-apple-system,sans-serif' }}>

      {/* Noise overlay */}
      <div className="fixed inset-0 pointer-events-none z-0 opacity-[0.025]"
        style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23n)\'/%3E%3C/svg%3E")', backgroundSize: '200px' }} />

      {/* Ambient glow */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute top-0 left-1/3 w-[600px] h-[400px] rounded-full blur-[120px]"
          style={{ background: 'radial-gradient(ellipse, rgba(200,117,51,0.06) 0%, transparent 70%)' }} />
      </div>

      {/* Header */}
      <header className="sticky top-0 z-50 flex items-center gap-4 px-5 py-3"
        style={{ background: 'rgba(8,15,30,0.85)', backdropFilter: 'blur(20px)', borderBottom: `1px solid ${C.border}` }}>

        {/* Logo */}
        <div className="flex items-center gap-3 mr-2">
          <div className="w-8 h-8 rounded-xl flex items-center justify-center"
            style={{ background: `linear-gradient(135deg, ${C.accent}30, ${C.accent}10)`, border: `1px solid ${C.accent}30` }}>
            <Zap className="w-4 h-4" style={{ color: C.accent }} />
          </div>
          <div className="leading-none">
            <p className="text-sm font-bold text-white tracking-tight">WIBE INTEL</p>
            <p className="text-[9px] font-mono uppercase tracking-widest mt-0.5" style={{ color: `${C.accent}90` }}>The Brain · 2026</p>
          </div>
        </div>

        {/* Nav */}
        <nav className="hidden sm:flex items-center gap-1 p-1 rounded-xl" style={{ background: 'rgba(255,255,255,0.04)', border: `1px solid ${C.border}` }}>
          {VIEWS.map(v => {
            const Icon = v.icon;
            const active = view === v.id;
            return (
              <button key={v.id} onClick={() => setView(v.id)}
                className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-medium transition-all"
                style={{
                  background: active ? C.accent : 'transparent',
                  color: active ? '#fff' : 'rgba(255,255,255,0.45)',
                  boxShadow: active ? `0 4px 12px ${C.accent}40` : 'none',
                }}>
                <Icon className="w-3.5 h-3.5" /> {v.label}
              </button>
            );
          })}
        </nav>

        {/* Right side */}
        <div className="ml-auto flex items-center gap-3">
          {/* AI Debrief button */}
          <a href="/debrief"
            className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-mono font-semibold transition-all"
            style={{ background: 'rgba(240,141,73,0.12)', border: '1px solid rgba(240,141,73,0.25)', color: '#F08D49' }}>
            <span className="w-1.5 h-1.5 rounded-full bg-[#F08D49] animate-pulse" />
            AI Debrief
          </a>
          <div className="hidden md:flex items-center gap-1.5 px-3 py-1.5 rounded-lg"
            style={{ background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.15)' }}>
            <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
            <span className="text-[10px] text-green-400 font-mono">LIVE</span>
          </div>
          <span className="text-[11px] text-white/30 font-mono hidden sm:block">
            {time.toLocaleTimeString('sl-SI', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
          </span>
          <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white"
            style={{ background: `linear-gradient(135deg, ${C.accent}, #a85f1f)` }}>Ž</div>
        </div>
      </header>

      {/* Mobile nav */}
      <div className="sm:hidden flex sticky top-[57px] z-40"
        style={{ background: 'rgba(8,15,30,0.95)', backdropFilter: 'blur(20px)', borderBottom: `1px solid ${C.border}` }}>
        {VIEWS.map(v => {
          const Icon = v.icon;
          const active = view === v.id;
          return (
            <button key={v.id} onClick={() => setView(v.id)}
              className="flex-1 flex flex-col items-center gap-1 py-3 text-[10px] font-mono transition-all"
              style={{ color: active ? C.accent : 'rgba(255,255,255,0.35)', borderBottom: `2px solid ${active ? C.accent : 'transparent'}` }}>
              <Icon className="w-4 h-4" /> {v.label}
            </button>
          );
        })}
      </div>

      {/* Main */}
      <main className="relative z-10 flex-1 px-4 sm:px-6 py-6 max-w-[1400px] mx-auto w-full">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl font-bold text-white tracking-tight">
              {view === 'admin' ? 'Admin Dashboard' : view === 'field' ? 'Field View' : 'HoReCa Baza'}
            </h1>
            <p className="text-[11px] text-white/30 font-mono mt-0.5">
              {new Date().toLocaleDateString('sl-SI', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg"
            style={{ background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.15)' }}>
            <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
            <span className="text-[10px] text-green-400 font-mono">LIVE DEMO</span>
          </div>
        </div>

        {view === 'admin'  && <AdminDashboard />}
        {view === 'field'  && <FieldView />}
        {view === 'horeca' && <HoRecaDB />}
      </main>

      <footer className="relative z-10 px-6 py-4 flex items-center justify-between"
        style={{ borderTop: `1px solid ${C.border}` }}>
        <a href="/" className="text-[11px] text-white/25 hover:text-orange-400 transition-colors font-mono flex items-center gap-1.5">
          ← pmi.wibe.si
        </a>
        <span className="text-[10px] text-white/15 font-mono">Wibe Intel v1.0 · Simulacija podatkov</span>
      </footer>
    </div>
  );
};
